import { useRef } from 'react';
import { useStorage } from '../hooks/use-storage';
import { usePaymentCancelledModal } from '../screens/payment/payment-cancelled-modal';
import { usePaymentConfirmedModal } from '../screens/payment/payment-confirmed-modal';
import { usePaymentOfflineModal } from '../screens/payment/payment-offline-modal';
import { usePaymentSelectOrdersInstructionsModal } from '../screens/payment/payment-select-orders-instructions-modal';
import { usePaymentWalletModal } from '../screens/payment/payment-wallet-modal';
import { PaymentType, WalletType } from '../utils/manager/card-manager';
import { usePaymentCardWorkflow } from './payment-card-workflow';
import { usePaymentPixWorkflow } from './payment-pix-workflow';
import { usePaymentSelectionWorkflow } from './payment-selection-workflow';
import { isRecentPayment } from '../utils/manager/payment-manager';

function usePaymentWorkflow() {
  const { preparePaymentCardWorkflow, showPaymentCardWorkflow } =
    usePaymentCardWorkflow();
  const { preparePaymentPixWorkflow, showPaymentPixWorkflow } =
    usePaymentPixWorkflow();
  const { preparePaymentSelectionWorkflow, showPaymentSelectionWorkflow } =
    usePaymentSelectionWorkflow();
  const { preparePaymentWalletModal, showPaymentWalletModal } =
    usePaymentWalletModal();
  const { showPaymentOfflineModal } = usePaymentOfflineModal();
  const { showPaymentSelectOrdersInstructionsModal } =
    usePaymentSelectOrdersInstructionsModal();
  const { showPaymentConfirmedModal } = usePaymentConfirmedModal();
  const { showPaymentCancelledModal } = usePaymentCancelledModal();

  const { setPayment, paymentRef, spotRef } = useStorage();

  const prepareOnConfirmRef = useRef();

  async function prepare({ selectedOrders, updateOrders }) {
    if (paymentRef.current != null) {
      const params = {
        orders: paymentRef.current.orders,
        tipAmount: paymentRef.current.tip,
      };

      if (
        paymentRef.current?.pix != null &&
        paymentRef.current?.status === 'paying'
      ) {
        await preparePaymentPixWorkflow(params);
      }

      if (
        paymentRef.current?.card?.wallet != null &&
        paymentRef.current?.status === 'paying'
      ) {
        await preparePaymentWalletModal({
          ...params,
          wallet: paymentRef.current.card.wallet,
        });
      }

      if (
        paymentRef.current?.card != null &&
        paymentRef.current?.card?.wallet == null &&
        paymentRef.current?.status === 'paying'
      ) {
        await preparePaymentCardWorkflow(params);
      }
    }

    await preparePaymentSelectionWorkflow({ selectedOrders, updateOrders });
  }

  async function show(
    { showBackButton, autoLoaded, prepareOnPreConfirm, prepareOnPreDeny } = {
      autoLoaded: false,
    }
  ) {
    let result;
    prepareOnConfirmRef.current = prepareOnPreConfirm;

    if (
      paymentRef.current == null ||
      !['paying', 'paid'].includes(paymentRef.current?.status)
    ) {
      let repeat = false;
      setPayment(null);

      /* eslint-disable no-await-in-loop */
      do {
        result = await showPaymentSelectionWorkflow({
          showBackButton,
          autoLoaded,
          prepareOnPreConfirm: selectionWorkflowPepareOnConfirm,
          prepareOnPreDeny,
          wasBackButtonClicked: repeat,
        });

        result.isDismissed = result.isDenied;
        result.isDenied = false;

        repeat = false;

        if (result.isEmptyOrders) {
          return result;
        }

        if (result.isConfirmed) {
          if (result.amount === 'empty') {
            result = await showPaymentSelectOrdersInstructionsModal();
          } else if (result.method === 'offline') {
            result = await showPaymentOfflineModal();
            result.askForAttendant = true;
          } else if (result.method === PaymentType.PIX) {
            result = await showPaymentPixWorkflow();
            result.isPayment = true;
          } else if (
            [WalletType.GOOGLE_PAY, WalletType.APPLE_PAY].includes(
              result.method
            )
          ) {
            result = await showPaymentWalletModal();
            result.isPayment = true;
          } else if (result.method === PaymentType.CARD) {
            result = await showPaymentCardWorkflow();
            result.isPayment = true;
          }
        }

        repeat = result.isDenied && paymentRef.current?.status !== 'paid';
      } while (repeat);
    } else if (paymentRef.current?.status === 'paying') {
      const params = {
        autoLoaded,
        prepareOnPreConfirm,
      };

      if (paymentRef.current?.pix != null) {
        result = await showPaymentPixWorkflow(params);
      }

      if (paymentRef.current?.card?.wallet != null) {
        result = await showPaymentWalletModal(params);
      }

      if (
        paymentRef.current?.card != null &&
        paymentRef.current?.card?.wallet == null
      ) {
        result = await showPaymentCardWorkflow(params);
      }

      result.isPayment = true;
    }

    if (paymentRef.current?.status === 'paid') {
      if (isRecentPayment(paymentRef.current)) {
        await showPaymentConfirmedModal();
      }

      setPayment(null);
    }

    if (!result?.askForAttendant) {
      // if (
      //   (result?.isDismissed || result?.isDenied) &&
      //   spotRef.current?.prepaid
      // ) {
      //   await showLostChangesModal({ forceLostMessage: true });
      // } else if (result?.isDenied && autoLoaded) {
      //   await showPaymentCancelledModal();
      // }

      if (result?.isDenied && autoLoaded && !spotRef.current?.prepaid) {
        await showPaymentCancelledModal();
      }
    }

    return result;
  }

  async function selectionWorkflowPepareOnConfirm(params) {
    const { method } = params;

    if (method === PaymentType.PIX) {
      // await refreshPaymentPix({ payment: paymentRef.current});
      await preparePaymentPixWorkflow(params);
    }

    if (method === WalletType.GOOGLE_PAY || method === WalletType.APPLE_PAY) {
      await preparePaymentWalletModal({
        ...params,
        wallet: method,
      });
    }

    if (method === PaymentType.CARD) {
      await preparePaymentCardWorkflow(params);
    }

    prepareOnConfirmRef.current?.call(this, params);
  }

  return {
    preparePaymentWorkflow: prepare,
    showPaymentWorkflow: show,
  };
}

export { usePaymentWorkflow };
